import type { LocaleCopy } from "@/lib/i18n/copy";

/**
 * Контент-слой главной eventboutique.pt (BTQ-47, шаг A2 карты v2).
 *
 * Не путать с `home-eb.ts` — там блочная схема португальской главной из прежней
 * архитектуры. Карта v2 понизила блочный рендерер до именованного исключения,
 * и текст оттуда переезжает СЮДА на шаге A4, пословно. Пока `home-eb.ts` лежит
 * нетронутым: A2 не пишет ни строки перевода.
 *
 * Каждый блок — самостоятельная пара локалей, поэтому непереведённый блок
 * помечается отдельно от переведённого. Механика и правила фолбэка — в
 * `src/lib/i18n/copy.ts`.
 *
 * Португальская сторона заполнена целиком, но НЕОДНОРОДНА по происхождению, и
 * это важно для редактора:
 *
 *   перенос — текст уже существовал и переставлен пословно: мета, герой,
 *     услуги, финальный CTA (из `home-eb.ts`); раздел про ателье и пять шагов
 *     процесса (из `pt-core/pt-pages-v1/06-eb-home.md`); поля формы, подписи
 *     селектов и сообщения (из `contact.ts`, те же поля той же формы);
 *
 *   драфт — текста не существовало, написан под нативного редактора: портфолио,
 *     команда, инвестиции, редакционный блок целиком, плюс двенадцать мелких
 *     ключей (подписи слайдов, строка-девиз, подписи кнопок и разделов).
 *
 * Драфт вычитан машинно на pt-PT, запрещённую лексику §A4, совпадение чисел с
 * английской стороной и длину массивов, но человеком не вычитан. Пакет для
 * редактора — `pt-core/pt-para-o-editor/`.
 */

/**
 * ── Португальская сторона ──
 *
 * Текст НЕ написан заново: он перенесён пословно из блочной схемы
 * `home-eb.ts`, где лежал с волны A. Это операция шага A4 карты v2
 * («переезжает пословно, без переписывания»), применённая к главной, потому
 * что показывать английскую страницу под /pt/ при готовом переводе
 * бессмысленно.
 *
 * Перенесено только то, у чего в блочной схеме есть ТОЧНЫЙ встречный слот.
 * Где его нет — ключ отсутствует, рендерится английский, и блок помечается
 * data-i18n-fallback="en". Придумывать недостающее нельзя: это был бы новый
 * перевод, а Фаза A его не пишет.
 *
 * Отдельное ограничение: массив, задающий число элементов на странице
 * (абзацы, карточки, шаги), переносится ЦЕЛИКОМ или не переносится вовсе.
 * Массив другой длины сломал бы структурный паритет пары — а он условие
 * приёмки Фазы A.
 *
 * Что осталось в `home-eb.ts` и сюда не поехало: FAQ из шести вопросов и блок
 * «Onde produzimos». В композиции английской главной встречного блока у них
 * нет, поэтому их место определяет Фаза B, а не этот перенос.
 */

export interface MetaCopy {
  title: string;
  description: string;
}

export interface HeroCopy {
  slideAlts: string[];
  eyebrow: string;
  h1: string;
  tagline: string;
  lede: string;
  ctaWork: string;
  ctaPlanning: string;
}

export interface StatementCopy {
  h2: string;
  paragraphs: string[];
}

export const META: LocaleCopy<MetaCopy> = {
  en: {
    title: "Destination Wedding Planner in Portugal | Event Boutique",
    description:
      "Event Boutique is a destination wedding planner and production atelier in Cascais, producing weddings across Lisbon, Sintra, the Algarve and Porto.",
  },
  pt: {
    title: "Event Boutique — Organização de Casamentos em Portugal",
    description:
      "Organizamos casamentos em Portugal para casais que chegam de fora: Lisboa, Sintra, Algarve, Porto, Douro, Alentejo, Comporta e Madeira. Produções a partir de 70 000 €.",
  },
};

export const HERO: LocaleCopy<HeroCopy> = {
  en: {
    slideAlts: [
      "Aerial view of red vintage car driving through cypress-lined vineyard estate",
      "Intimate embrace detail with lace wedding dress and calla lily bouquet",
      "Pastel peony and calla lily bouquet with wedding rings and invitation suite",
      "Elegant couple cutting white three-tier wedding cake at sunset",
      "Aerial view of vineyard wedding reception with long table and guests",
    ],
    eyebrow: "Luxury Weddings in Portugal",
    h1: "Destination weddings in Portugal, produced in full.",
    tagline: "One atelier behind every moment.",
    lede: "Event Boutique is a luxury wedding atelier for international couples choosing a destination wedding in Portugal. We create intentional, atmospheric celebrations shaped by architecture, light and emotion.",
    ctaWork: "Explore Our Work",
    ctaPlanning: "Begin Your Planning",
  },
  // Эйброу, H1 и лид — перенос из блочной схемы `home-eb.ts`. Подписи слайдов,
  // строка-девиз и обе кнопки встречного слота там не имели: это драфт,
  // написанный под нативного редактора. Подписи кнопок совпадают с теми же
  // кнопками в редакционном блоке — одна формулировка на страницу.
  pt: {
    slideAlts: [
      "Vista aérea de um carro clássico vermelho a percorrer uma quinta com vinha ladeada de ciprestes",
      "Pormenor de um abraço, com vestido de noiva em renda e ramo de jarros",
      "Ramo de peónias e jarros em tons pastel, com as alianças e o conjunto de convites",
      "Casal a cortar um bolo de casamento branco de três andares ao pôr do sol",
      "Vista aérea de um copo de água numa quinta com vinha, com mesa comprida e convidados",
    ],
    eyebrow: "Event Boutique",
    h1: "Organização de casamentos em Portugal, para casais que vêm de fora",
    // Второй абзац блока answerFirst: он о том, с кем мы работаем, — ровно роль
    // английского лида «for international couples choosing a destination wedding».
    lede: "Trabalhamos sobretudo com casais cujos convidados chegam de vários países, porque é aí que a diferença se nota: alojamento, transferes, um programa que dura mais do que uma noite e uma equipa no local desde a véspera. Para um casamento de um só dia, com convidados quase todos do país, uma equipa mais pequena serve melhor — e dizemo-lo na primeira conversa.",
    tagline: "Um atelier por trás de cada momento.",
    ctaWork: "Ver o nosso trabalho",
    ctaPlanning: "Começar o planeamento",
  },
};

export const STATEMENT: LocaleCopy<StatementCopy> = {
  en: {
    h2: "One atelier behind every moment.",
    paragraphs: [
      "Event Boutique is a private wedding atelier based in Cascais on the Portuguese Riviera. We work as a boutique production studio for international couples who see their wedding as a composed experience rather than a single day.",
      "As a luxury wedding planner in Portugal, our role goes beyond checklists and timelines. We design how light, space, sound and movement come together into an intentional, atmospheric celebration that feels both intimate and editorial.",
      // Внутри «architecture‑led» стоит неразрывный дефис U+2011, а не обычный.
      // Замена на ASCII-дефис изменила бы английский вывод побайтово.
      "Each project is limited in number and treated as a bespoke production. We apply structural precision to every decision, from architecture‑led layouts to curated partner teams, so that your destination wedding in Portugal feels effortless on the surface and meticulously engineered underneath.",
    ],
  },
  // Три абзаца из текста v1 главной (pt-pages-v1/06-eb-home.md): кто мы и три
  // слоя работы. Роль совпадает с английской — раздел про сам ателье. Заголовок
  // встречного слота не имеет: английский h2 повторяет строку-девиз, а её в
  // португальском тексте нет.
  pt: {
    // Английский h2 повторяет строку-девиз героя дословно; португальский
    // повторяет её так же.
    h2: "Um atelier por trás de cada momento.",
    paragraphs: [
      "A Event Boutique é um atelier de produção de casamentos com base em Cascais. Produzimos entre doze e quinze celebrações por ano, cada uma com dois a quatro dias de programa, para que a mesma equipa esteja presente em cada montagem. A equipa soma mais de dez anos de produção de eventos e trabalha no mercado português desde 2022.",
      "Um casamento produzido tem três camadas que costumam andar separadas. A escolha do espaço e dos fornecedores, o desenho visual do dia, e a direção do próprio dia, quando cento e vinte pessoas se movem entre a cerimónia, o jantar e a festa sem que ninguém repare no trabalho por trás disso.",
      "Trabalhamos as três com a mesma equipa. Para a família, isso significa um único interlocutor, um orçamento por rubrica em vez de dezenas de propostas soltas, e um guião do dia que os fornecedores conhecem antes de chegarem.",
    ],
  },
};


export interface PortfolioCopy {
  eyebrow: string;
  h2: string;
  lede: string;
  ctaPortfolio: string;
  alts: string[];
}

export const PORTFOLIO: LocaleCopy<PortfolioCopy> = {
  en: {
    eyebrow: "Portfolio",
    h2: "Our Beautiful Destination Weddings in Portugal",
    lede: "Our portfolio is a curated archive of high-end celebrations we have produced for couples from the US, UK and across Europe. Each gallery reflects our atelier's approach to premium event design, architectural event styling and the atmosphere of different Portuguese regions.",
    // В конце подписи стоит стрелка → (U+2192), а не ASCII-последовательность
    // "->". Замена изменила бы английский вывод побайтово.
    ctaPortfolio: "View Full Portfolio →",
    // Порядок подписей связан с порядком файлов в `images` компонента индексом:
    // девять картинок — девять alt, менять местами нельзя.
    alts: [
      "Wedding procession past a red domed palace in Portugal",
      "Black and white couple walking down ceremony aisle with petals under old tree",
      "Luxury wedding invitation suite with silk ribbon and cufflinks",
      "Couple portrait at a balcony under a vintage lantern, groom in black tie",
      "Aerial view of red vintage car driving through cypress avenue to white estate",
      "Couple kissing on palace steps under white hydrangea arch",
      "Couple with calla lily bouquet, bride in lace halter-neck dress",
      "Couple at stone balcony with white peacock on the railing",
      "Black and white bride ascending grand palace staircase with floral arch",
    ],
  },
  // Драфт для нативного редактора. Заголовки — в sentence case, как уже принято
  // в португальском контент-слое («O que fazemos»), а не Title Case английской
  // стороны: это норма португальской типографики.
  pt: {
    eyebrow: "Portefólio",
    h2: "Os nossos casamentos de destino em Portugal",
    lede: "O nosso portefólio é um arquivo curado de celebrações de gama alta que produzimos para casais dos Estados Unidos, do Reino Unido e de vários países da Europa. Cada galeria reflete a abordagem do atelier ao design de eventos premium, ao estilismo de eventos de base arquitetónica e à atmosfera das diferentes regiões portuguesas.",
    ctaPortfolio: "Ver portefólio completo →",
    alts: [
      "Cortejo de casamento a passar junto a um palácio vermelho com cúpula, em Portugal",
      "Fotografia a preto e branco de um casal a descer o corredor da cerimónia com pétalas, sob uma árvore antiga",
      "Conjunto de convites de casamento de luxo com fita de seda e botões de punho",
      "Retrato do casal numa varanda sob uma lanterna antiga, com o noivo de black tie",
      "Vista aérea de um carro clássico vermelho a percorrer uma alameda de ciprestes até uma quinta branca",
      "Casal a beijar-se na escadaria de um palácio, sob um arco de hortênsias brancas",
      "Casal com um ramo de jarros, a noiva com vestido de renda de alças ao pescoço",
      "Casal numa varanda de pedra com um pavão branco no gradeamento",
      "Fotografia a preto e branco da noiva a subir a escadaria principal de um palácio, com arco floral",
    ],
  },
};

export interface ServicesCopy {
  eyebrow: string;
  h2: string;
  lede: string;
  services: { title: string; desc: string }[];
  learnMore: string;
  viewAll: string;
}

export const SERVICES: LocaleCopy<ServicesCopy> = {
  en: {
    eyebrow: "Services",
    h2: "Luxury Wedding Planning Services in Portugal",
    // В лиде стоит длинное тире U+2014, а в подписях ссылок — стрелка U+2192.
    // Замена на ASCII-аналоги изменила бы английский вывод побайтово.
    lede: "A suite of services designed as a single, continuous journey — from concept and design to full-scale production and guest experience.",
    // Порядок совпадает с порядком адресов в ServicesSection.tsx: связь по индексу.
    services: [
      { title: "Wedding Planning & Management", desc: "Full-cycle planning and management for destination weddings in Portugal, from venue sourcing and budget structure to schedules, contracts and on-site leadership." },
      { title: "Creative Direction", desc: "Architecture-informed creative direction, moodboards and 3D visualisations that translate your vision into coherent, intentional atmospheres." },
      { title: "Event Production", desc: "Full-scale event production studio for structural builds, technical logistics, lighting, sound and all backstage systems supporting your celebration." },
      { title: "Wedding Coordination", desc: "Operational oversight and calm, senior on-the-day leadership to ensure every moving part follows the agreed structure." },
      { title: "Private Concierge", desc: "International guest experience: VIP transfers, curated local experiences and discreet hospitality for overseas marriage celebrations." },
    ],
    // Одна строка на обе ветки аккордеона — мобильную и десктопную.
    learnMore: "Learn more →",
    viewAll: "View All Services →",
  },
  // Таблица «O que fazemos» блочной схемы — те же пять услуг в том же порядке,
  // что и в компоненте: планирование, креатив, продакшн, координация, консьерж.
  // Заголовок раздела и подписи ссылок встречного слота не имеют.
  pt: {
    eyebrow: "Serviços",
    h2: "O que fazemos",
    // В английском лиде стоит длинное тире; в португальской прозе оно правилами
    // бренд-войса запрещено, поэтому связка выражена словами.
    lede: "Um conjunto de serviços pensado como um percurso único e contínuo, que vai do conceito e do desenho até à produção integral e à experiência dos convidados.",
    learnMore: "Saber mais →",
    viewAll: "Ver todos os serviços →",
    services: [
      { title: "Planeamento integral", desc: "Do primeiro briefing ao fim da festa: procura de espaço, estrutura de orçamento, cronogramas, contratos e direção no local." },
      { title: "Direção criativa", desc: "Conceito informado pela arquitetura do espaço, moodboards e visualizações 3D que traduzem a ideia numa atmosfera coerente." },
      { title: "Produção", desc: "Construções, logística técnica, luz, som e todos os sistemas de bastidores que sustentam a celebração." },
      { title: "Coordenação", desc: "Supervisão operacional e direção sénior no próprio dia, para que cada peça siga a estrutura acordada." },
      { title: "Concierge", desc: "Experiência dos convidados internacionais: transferes, programa local e hospitalidade discreta antes e depois do casamento." },
    ],
  },
};

export interface ProcessCopy {
  eyebrow: string;
  h2: string;
  /** Пять шагов процесса. Номера («01»…«05») — нумерация, а не текст: они
   *  живут в компоненте и связаны с этим массивом индексом. */
  steps: { title: string; desc: string }[];
}

export const PROCESS: LocaleCopy<ProcessCopy> = {
  en: {
    eyebrow: "The Journey",
    h2: "Our Process From Concept to Legacy",
    steps: [
      {
        title: "Discovery",
        desc:
          "Discovery is where we learn how you live, host and celebrate. We explore your story, aesthetic references, cultural context and what a destination wedding in Portugal should feel like for you and your guests. Here we also define parameters such as guest count, season, regions of interest and investment range.",
      },
      {
        title: "Curation",
        desc:
          "During Curation, we translate your priorities into a curated set of real options. Instead of overwhelming you with venues and suppliers, we present a structured matrix of scenarios across Lisbon & Cascais, Sintra, the Algarve, Porto and the Douro Valley. Each scenario includes logistics, budget ranges and aesthetic implications, so you can choose with clarity.",
      },
      {
        title: "Design",
        desc:
          "In Design, we move from concept to structural precision. We develop layouts, lighting concepts, timeline pacing, material palettes and service choreography. This is where our approach to premium event design and architectural event styling becomes visible on paper and in prototypes, while staying fully aligned with technical feasibility.",
      },
      {
        title: "Production",
        desc:
          "Production is the backstage of your celebration. We coordinate structural installations, technical teams, safety, transport, staffing and all vendor movements. As a full-scale production studio, we ensure every element of your destination wedding in Portugal is supported by a robust, invisible framework.",
      },
      {
        title: "Legacy",
        desc:
          "Legacy is the way your celebration continues after the last guest has left. We consider how photography, film and narratives will capture your day; we align schedules to support editorial documentation and help you close the loop on any post-event details. Our goal is a cohesive archive that reflects the structural care invested in every moment.",
      },
    ],
  },
  // Пять шагов из текста v1 главной (pt-pages-v1/06-eb-home.md) — тот же
  // порядок и та же роль нумерованного процесса.
  //
  // ⚠️ Пятый шаг у двух языков говорит РАЗНОЕ: английский «Legacy» — про то, что
  // остаётся после праздника (съёмка, архив), португальский «O dia» — про сам
  // день. Тексты писались независимо, и это единственное место на странице, где
  // секции не совпадают по содержанию. Свести их — работа редактора в Фазе B,
  // выдумывать здесь пятый абзац нельзя.
  pt: {
    eyebrow: "O percurso",
    h2: "O nosso processo, da primeira conversa ao dia",
    steps: [
      { title: "Primeira conversa", desc: "Falamos sobre a data, o número de convidados, a região e o que imaginam para o dia. Dessa conversa sai um enquadramento de orçamento realista." },
      { title: "Seleção de espaços", desc: "Apresentamos quintas e palácios que servem o formato, com capacidade, restrições e custo de aluguer. As visitas são organizadas em bloco." },
      { title: "Desenho e orçamento", desc: "Fixamos o desenho do dia e o orçamento por rubrica, com o que está incluído e o que fica em aberto até à contagem final de convidados." },
      { title: "Produção", desc: "Contratos, provas de menu, cronograma, transporte e alojamento dos convidados. É a fase longa, e é onde o trabalho evita as surpresas." },
      { title: "O dia", desc: "A equipa está no espaço desde a montagem até ao fim da festa, com o guião minuto a minuto e um único ponto de contacto para a família." },
    ],
  },
};

export interface TeamCopy {
  eyebrow: string;
  h2: string;
  body: string;
  photoAlt: string;
  cta: string;
}

export const TEAM: LocaleCopy<TeamCopy> = {
  en: {
    eyebrow: "Our Team",
    h2: "The Atelier Behind Every Detail",
    body: "Event Boutique is led by a team of experienced producers and designers who specialise in destination wedding Portugal projects for international clients. Our backgrounds span architecture, production, fashion and hospitality, allowing us to think like both designers and operators. We are a small, senior team of luxury wedding planners in Portugal, directly involved in each project.",
    // В alt стоит длинное тире U+2014, в подписи ссылки — стрелка U+2192.
    // Замена на ASCII-аналоги изменила бы английский вывод побайтово.
    photoAlt: "Event Boutique production team — three women in elegant black attire",
    cta: "Meet the Team →",
  },
  // Драфт для нативного редактора. Фраза про «mais de dez anos... desde 2022»
  // из первого захода снята: английская сторона такого утверждения не делает,
  // а подпись счётчика «Anos de experiência» его и так несёт.
  pt: {
    eyebrow: "A nossa equipa",
    h2: "O atelier por trás de cada pormenor",
    body: "A Event Boutique é dirigida por uma equipa de produtores e designers com experiência em casamentos de destino em Portugal para clientes internacionais. Os nossos percursos passam pela arquitetura, pela produção, pela moda e pela hotelaria, o que nos permite pensar ao mesmo tempo como criadores e como operadores. Somos uma equipa pequena e sénior de organização de casamentos em Portugal, envolvida diretamente em cada projeto.",
    photoAlt: "Equipa de produção da Event Boutique, três mulheres com trajes pretos elegantes",
    cta: "Conhecer a equipa →",
  },
};

export interface InvestmentCopy {
  eyebrow: string;
  h2: string;
  paragraphs: string[];
  /** Подписи трёх счётчиков; порядок связан с STATS в компоненте. */
  statLabels: string[];
}

export const INVESTMENT: LocaleCopy<InvestmentCopy> = {
  en: {
    eyebrow: "Investment",
    // В JSX здесь стоял амперсанд как есть, без сущности &amp;.
    h2: "Investment & Approach",
    paragraphs: [
      "Event Boutique works at the level where structure, design and hospitality can be fully aligned. Boutique luxury productions begin at €70,000. Most of our couples invest between €90,000 and €250,000+ for multi-day, high-end celebrations with full-scale production and curated supplier teams.",
      "Each project is limited in number and treated as a bespoke production to ensure that every destination wedding in Portugal receives the same structural precision and calm, attentive leadership.",
    ],
    statLabels: [
      "Celebrations Produced",
      "Years of Destination Wedding Expertise",
      "Guests Hosted",
    ],
  },
  // Драфт для нативного редактора. Суммы совпадают с английской стороной:
  // 70 000 € начало, 90 000 € – 250 000 € и выше типичный диапазон.
  // Подписи счётчиков связаны с STATS в InvestmentSection.tsx по индексу.
  pt: {
    eyebrow: "Investimento",
    h2: "Investimento e abordagem",
    paragraphs: [
      "A Event Boutique trabalha no nível em que a estrutura, o design e a hospitalidade podem estar plenamente alinhados. As produções de luxo do atelier começam nos 70 000 €, e a maioria dos casais investe entre 90 000 € e 250 000 € ou mais, em celebrações de vários dias com produção integral e equipas de fornecedores reunidas caso a caso.",
      "O número de projetos por ano é limitado e cada um é tratado como uma produção feita à medida, para que todos os casamentos de destino em Portugal recebam a mesma precisão estrutural e a mesma direção serena e atenta.",
    ],
    statLabels: [
      "Celebrações produzidas",
      "Anos de experiência em casamentos de destino",
      "Convidados recebidos",
    ],
  },
};

export interface FinalCtaOption {
  /** Уходит в Notion. Английское, не трогать. */
  value: string;
  label: string;
}

export interface FinalCtaCopy {
  h2: string;
  intro: string;
  duplicateTitle: string;
  duplicateBody: string;
  thanksTitle: string;
  thanksBody: string;
  formEyebrow: string;
  fields: {
    fullName: string;
    email: string;
    phone: string;
    phonePlaceholder: string;
    country: string;
    preferredMonth: string;
    preferredMonthPlaceholder: string;
    guestCount: string;
    guestCountPlaceholder: string;
    budget: string;
    budgetPlaceholder: string;
    location: string;
    locationPlaceholder: string;
    message: string;
    messagePlaceholder: string;
  };
  budgetOptions: FinalCtaOption[];
  locationOptions: FinalCtaOption[];
  limitedProjects: string;
  submit: string;
  submitting: string;
  responseTime: string;
  toastSuccess: string;
  toastFailed: string;
  toastCaptchaWait: string;
  toastCaptchaFailed: string;
}

// Значения опций уходят в Notion и обязаны остаться английскими: набор
// зафиксирован edge-функцией, португальская подпись в базе сломала бы
// отчётность. Переводится только label — как на странице контактов.
// Имена с префиксом FINAL_CTA_, чтобы не столкнуться с константами соседних
// блоков в этом же файле.
const FINAL_CTA_BUDGET_VALUES = [
  "€70,000 – €100,000",
  "€100,000 – €150,000",
  "€150,000 – €250,000",
  "€250,000+",
  // TK-529: вариант «не определились»; поле бюджета необязательное.
  "Not sure yet",
];
const FINAL_CTA_LOCATION_VALUES = [
  "Algarve",
  "Lisbon & Cascais",
  "Sintra",
  "Porto",
  "Douro Valley",
  "Not sure yet",
];

export const FINAL_CTA: LocaleCopy<FinalCtaCopy> = {
  en: {
    h2: "Begin Your Celebration",
    intro:
      "Each project begins with a private consultation to understand your vision, aesthetic, and expectations.",
    duplicateTitle: "Already Received",
    duplicateBody: "You have already submitted an inquiry. Our team will be in touch shortly.",
    thanksTitle: "Thank You",
    thanksBody:
      "Your account has been created. Check your email for login details to access your Client Portal.",
    formEyebrow: "Request a Private Consultation",
    fields: {
      fullName: "Full Name",
      email: "Email",
      phone: "Phone Number",
      phonePlaceholder: "+1 (555) 000-0000",
      country: "Country",
      preferredMonth: "Preferred Month",
      preferredMonthPlaceholder: "e.g. June 2026",
      guestCount: "Estimated Guest Count",
      // Внутри «50–300» стоит короткое тире U+2013, а не дефис.
      guestCountPlaceholder: "50–300",
      budget: "Investment Range",
      budgetPlaceholder: "Select range",
      location: "Preferred Region",
      locationPlaceholder: "Select region",
      message: "Tell Us About Your Vision",
      // Тире перед «style» — длинное, U+2014.
      messagePlaceholder: "Share anything about your celebration — style, inspiration, special requests...",
    },
    // Подпись равна значению — на английской стороне атрибут value у <option>
    // не рендерится вовсе, и разметка остаётся прежней побайтово.
    budgetOptions: FINAL_CTA_BUDGET_VALUES.map((v) => ({ value: v, label: v })),
    locationOptions: FINAL_CTA_LOCATION_VALUES.map((v) => ({ value: v, label: v })),
    limitedProjects:
      "We accept a limited number of weddings each season to ensure the highest level of dedication.",
    submit: "Submit Inquiry",
    submitting: "Sending...",
    responseTime: "Response within 24–48 hours",
    toastSuccess: "Success! Your account is created. Check your email for login details.",
    toastFailed: "Failed to send. Please try again.",
    toastCaptchaWait: "Please wait for security verification to complete.",
    toastCaptchaFailed: "Security verification failed. Please refresh and try again.",
  },
  // Из блока cta блочной схемы. Поля формы, тосты и подписи селектов встречного
  // слота на этой странице не имеют — они живут в контент-слое контактов и
  // сюда не переносятся: это была бы не перестановка, а новое решение.
  // Заголовок и текст — из блока cta схемы `home-eb.ts`. Поля формы, подписи
  // селектов, тосты и сообщения взяты из контент-слоя контактов
  // (`contact.ts`): это ТЕ ЖЕ поля той же формы, уже написанные по-португальски
  // и прошедшие прод. Значения селектов остаются каноническими английскими —
  // они уходят в Notion, и португальская подпись в базе сломала бы отчётность.
  pt: {
    h2: "Falar sobre o vosso casamento",
    intro:
      "Com a data, o número de convidados e a região conseguimos dizer o que é possível e em que intervalo se situa, antes de qualquer proposta formal.",
    duplicateTitle: "Pedido já recebido",
    duplicateBody: "Já recebemos um pedido com este e-mail. A equipa entra em contacto em breve.",
    thanksTitle: "Obrigado",
    thanksBody:
      "A conta foi criada. Verifique o e-mail: enviámos os dados de acesso ao portal de cliente.",
    formEyebrow: "Pedido de contacto",
    fields: {
      fullName: "Nome",
      email: "E-mail",
      phone: "Telefone",
      phonePlaceholder: "+351 900 000 000",
      country: "País",
      preferredMonth: "Data ou mês do casamento",
      preferredMonthPlaceholder: "por exemplo, junho de 2026",
      guestCount: "Número de convidados",
      guestCountPlaceholder: "50 a 300",
      budget: "Orçamento previsto",
      budgetPlaceholder: "Escolha o intervalo",
      location: "Região",
      locationPlaceholder: "Escolha a região",
      message: "O que imaginam para o dia",
      messagePlaceholder: "Conte-nos o que já está decidido e o que ainda está em aberto.",
    },
    // ⚠️ У формы главной ЧЕТЫРЕ интервала, а у формы контактов — три. Список
    // обязан совпадать по длине с английским, иначе на странице пропадает один
    // <option> и структурный паритет пары ломается. Ровно это и произошло на
    // первом заходе: skeleton-parity показал 645 узлов против 646.
    budgetOptions: [
      { value: FINAL_CTA_BUDGET_VALUES[0], label: "70 000 € – 100 000 €" },
      { value: FINAL_CTA_BUDGET_VALUES[1], label: "100 000 € – 150 000 €" },
      { value: FINAL_CTA_BUDGET_VALUES[2], label: "150 000 € – 250 000 €" },
      { value: FINAL_CTA_BUDGET_VALUES[3], label: "Mais de 250 000 €" },
      { value: FINAL_CTA_BUDGET_VALUES[4], label: "Ainda não sei" },
    ],
    locationOptions: [
      { value: FINAL_CTA_LOCATION_VALUES[0], label: "Algarve" },
      { value: FINAL_CTA_LOCATION_VALUES[1], label: "Lisboa e Cascais" },
      { value: FINAL_CTA_LOCATION_VALUES[2], label: "Sintra" },
      { value: FINAL_CTA_LOCATION_VALUES[3], label: "Porto" },
      { value: FINAL_CTA_LOCATION_VALUES[4], label: "Vale do Douro" },
      { value: FINAL_CTA_LOCATION_VALUES[5], label: "Ainda não sei" },
    ],
    limitedProjects:
      "Aceitamos um número limitado de casamentos por época, para que a mesma equipa esteja presente em cada montagem.",
    submit: "Enviar pedido",
    submitting: "A enviar...",
    responseTime: "Resposta em um dia útil",
    toastSuccess: "Pedido enviado. Verifique o e-mail para os dados de acesso.",
    toastFailed: "Não foi possível enviar. Tente novamente.",
    toastCaptchaWait: "Aguarde a verificação de segurança.",
    toastCaptchaFailed: "A verificação de segurança falhou. Recarregue a página e tente novamente.",
  },
};

export interface EditorialLocationCard {
  name: string;
  subtitle: string;
  desc: string;
  linkText: string;
  alt: string;
}

export interface EditorialInsight {
  label: string;
  text: string;
}

export interface EditorialProcessStep {
  title: string;
  text: string;
}

export interface EditorialCopy {
  locationsH2: string;
  locationsLede: string;
  cards: EditorialLocationCard[];
  viewAllLocations: string;
  mastheadIssue: string;
  mastheadPlace: string;
  mastheadBrand: string;
  mastheadSpecial: string;
  whyHeadlineLine1: string;
  whyHeadlineLine2: string;
  whyDeck: string;
  whyIntro: string;
  insights: EditorialInsight[];
  whyBridge1: string;
  whyBridge2: string;
  whyClose: string;
  philosophyLabel: string;
  philosophyHeadlineLine1: string;
  philosophyHeadlineLine2: string;
  philosophyParagraphs: string[];
  philosophyPrinciples: string[];
  brideAlt: string;
  brideCaption: string;
  footerRegions: string;
  footerContinued: string;
  legalEyebrow: string;
  legalH2: string;
  legalIntro: string;
  legalLead: string;
  legalLinkText: string;
  legalTail: string;
  faqLead: string;
  faqLinkText: string;
  faqTail: string;
  journeyH2: string;
  journeyIntro: string;
  ctaWork: string;
  ctaPlanning: string;
  steps: EditorialProcessStep[];
}

export const EDITORIAL: LocaleCopy<EditorialCopy> = {
  en: {
    locationsH2: "Curated Locations Across Portugal",
    locationsLede:
      "Event Boutique structures its work around a set of carefully chosen regions, each with a dedicated focus.",
    cards: [
      {
        name: "Lisbon",
        subtitle: "EUROPEAN GRANDEUR",
        desc:
          "Lisbon offers rooftop terraces, historic palaces and contemporary hotels with views over the Tagus River. Ideal for couples seeking premium event design with minimal travel complexity.",
        linkText: "Explore Lisbon →",
        alt: "Lisbon wedding destination",
      },
      {
        name: "Cascais",
        subtitle: "COASTAL ELEGANCE",
        desc:
          "Cascais sits at the heart of the Portuguese Riviera: private oceanfront villas, elegant seaside venues and an unmistakable Old Money atmosphere. Atlantic views, golden light and refined ease.",
        linkText: "Explore Cascais →",
        alt: "Cascais wedding destination",
      },
      {
        name: "Sintra",
        subtitle: "PALACES & FORESTS",
        desc:
          "Architecture and landscape converge in theatrical fashion. Palaces and heritage estates sit within UNESCO-listed forests, creating immersive drama for evening ceremonies and candlelit receptions.",
        linkText: "Discover Sintra →",
        alt: "Sintra wedding destination",
      },
      {
        name: "Porto",
        subtitle: "RIVERSIDE CHARM",
        desc:
          "Layered urban texture: riverfront buildings, tiled churches, warehouses converted into design hotels and wine lodges overlooking the Douro. Wine culture meets riverside sophistication.",
        linkText: "Explore Porto →",
        alt: "Porto wedding destination",
      },
      {
        name: "Algarve",
        subtitle: "CLIFF & COAST",
        desc:
          "Golden beaches, dramatic cliffs and the Atlantic Ocean. Barefoot ease balanced with structural precision — wind, sun, access paths and tides all shape how celebrations are designed.",
        linkText: "Plan Algarve →",
        alt: "Algarve wedding destination",
      },
      {
        name: "Douro Valley",
        subtitle: "VINEYARD ESTATES",
        desc:
          "Terraced vineyards, historic quintas and river views that invite slower, more immersive weekends. Wine culture and architectural heritage converge in this UNESCO landscape.",
        linkText: "Explore Douro →",
        alt: "Douro Valley wedding destination",
      },
    ],
    viewAllLocations: "View All Locations",
    mastheadIssue: "No. 47 · Spring/Summer",
    mastheadPlace: "Portugal · MMXXV",
    mastheadBrand: "Event Boutique",
    mastheadSpecial: "Wedding Special",
    whyHeadlineLine1: "Why the World's Most Discerning Couples",
    whyHeadlineLine2: "Are Choosing Portugal",
    whyDeck: "Light · Heritage · Architecture · Calm Luxury",
    whyIntro:
      "Portugal has become one of the most sought-after settings for luxury destination weddings, particularly among couples from the US, UK and wider Europe who value atmosphere over spectacle. The country combines layered history, diverse landscapes and a hospitality culture that is both warm and discreet.",
    insights: [
      {
        label: "01 — Light",
        text:
          "The quality of light is Portugal's greatest asset. Along the coast, soft Atlantic brightness and long golden hours define open-air ceremonies. Inland, filtered light through forests creates intimate atmosphere.",
      },
      {
        label: "02 — Heritage",
        text:
          "Centuries-old palaces, quintas and estates with azulejo tilework, carved stone, dramatic staircases and cloistered gardens. We build bespoke production that respects and frames these elements.",
      },
      {
        label: "03 — Calm Luxury",
        text:
          "Serious service and craftsmanship without unnecessary formality. Celebrations that feel effortless to experience and rigorous behind the scenes.",
      },
    ],
    whyBridge1:
      "From the Portuguese Riviera to the mist over Sintra's forests and the slow rhythm of the Douro Valley, each region offers a different emotional register. Portugal is not a generic backdrop, but a structural canvas for premium event design.",
    whyBridge2:
      "Lisbon and Porto are well-connected international gateways, yet once guests arrive, heritage palaces, oceanfront villas, vineyards and modern design hotels coexist within a few hours' drive.",
    whyClose:
      "This aligns closely with our own philosophy of intentional, quietly confident celebrations — where every element has been considered, placed and timed with architectural precision.",
    philosophyLabel: "Our Philosophy",
    philosophyHeadlineLine1: "How We Think About Luxury",
    philosophyHeadlineLine2: "Wedding Production",
    philosophyParagraphs: [
      "At Event Boutique, the work is wedding production. Production is the orchestration of space, light, sound, movement and hospitality into a single, coherent experience.",
      "Luxury, in this context, is not about excess. It is about structural precision and intentional restraint.",
      "Each project is built from the ground up, tailored to one couple, one guest constellation and one location.",
    ],
    philosophyPrinciples: [
      "Fewer design elements, chosen and placed with purpose.",
      "Focused timelines that respect human energy.",
      "Clear, respectful communication.",
      "Technical systems that disappear into the background.",
    ],
    brideAlt: "Bride descending palace staircase — editorial portrait",
    brideCaption: "Palace of Queluz, Sintra — Styled by Event Boutique",
    footerRegions: "Lisbon · Cascais · Sintra · Porto · Algarve · Douro Valley",
    // `&nbsp;` из JSX стал настоящим U+00A0: React отдаёт его тем же байтом,
    // что и сущность, — английский вывод не сдвигается.
    footerContinued: "Continued on pages 4–5 →   Event Boutique · Est. 2015",
    legalEyebrow: "Important Information",
    // `&amp;` из JSX стал настоящим `&` — React экранирует его обратно.
    legalH2: "Legal Clarity & Logistics",
    legalIntro:
      "Legal requirements and logistics are often where anxiety concentrates for international couples.",
    legalLead: "For legal structure — explore our",
    legalLinkText: "Legal Requirements",
    legalTail: "guide.",
    faqLead: "For practical matters — our",
    faqLinkText: "FAQ",
    faqTail: "offers structured answers.",
    journeyH2: "Begin Your Journey",
    journeyIntro:
      "If you recognise your own values in this approach — the next step is simple.",
    ctaWork: "Explore Our Work",
    ctaPlanning: "Begin Your Planning",
    steps: [
      {
        title: "Discovery",
        text:
          "During Discovery, we spend time understanding your life, your aesthetic references and your priorities for how the celebration should feel. We also discuss practical questions: which regions you are drawn to, how far guests are likely to travel, and what level of formality feels natural.",
      },
      {
        title: "Curation",
        text:
          "In Curation, we narrow an entire country down to a set of viable pathways: two or three locations that genuinely match your brief, each with sample budgets and logistical implications. Instead of sending a long list of links, we act as editors, allowing you to compare real scenarios.",
      },
      {
        title: "Design & Production",
        text:
          "Through Design and Production, we combine architectural event styling with the reality of build schedules, permissions and technical constraints. We know from experience that couples planning from abroad need both creativity and clear structure: moodboards, floorplans, lighting diagrams and critical paths that make it easy to visualise what will happen, and when.",
      },
      {
        title: "Legacy",
        text:
          "Finally, in Legacy, we think beyond the event days themselves. The way photography is scheduled, the angles chosen, the timeline of speeches and transitions — all of these determine how your celebration will live in memory and in images.",
      },
    ],
  },
  // Драфт для нативного редактора, собран из трёх частей и выправлен по находкам
  // враждебной проверки: сняты факты, которых нет на английской стороне
  // (время в пути до Кашкайша и Douro, сезон Алгарве, правила Синтры,
  // аэропорт Лиссабона), развёрнуты безглагольные обрубки в insights,
  // унифицирован термин «estilismo de eventos de base arquitetónica».
  //
  // ⚠️ footerContinued: английская сторона несёт «Est. 2015», и это нарушает
  // origin lock §11 (португальский рынок с 2022). Португальская версия
  // ошибку не воспроизводит. Английскую не правлю — она вне скоупа Фазы A.
  pt: {
    locationsH2: "Regiões onde produzimos em Portugal",
    locationsLede:
      "A Event Boutique organiza o trabalho em torno de um conjunto de regiões escolhidas, cada uma com um foco próprio.",
    cards: [
      {
        name: "Lisboa",
        subtitle: "GRANDEZA EUROPEIA",
        desc:
          "Lisboa reúne terraços sobre a cidade, palácios históricos e hotéis contemporâneos com vista para o Tejo. É a escolha indicada para casais que procuram design de eventos premium com pouca complexidade de deslocação.",
        linkText: "Explorar Lisboa →",
        alt: "Lisboa como destino de casamento: palácio e terraço com vista para o Tejo",
      },
      {
        name: "Cascais",
        subtitle: "ELEGÂNCIA COSTEIRA",
        desc:
          "Cascais está no centro da Riviera Portuguesa, com moradias privadas de frente para o oceano, espaços à beira-mar e um ambiente inconfundível de Old Money. A região junta vista atlântica, luz dourada e uma leveza contida.",
        linkText: "Explorar Cascais →",
        alt: "Cascais como destino de casamento: costa atlântica da Riviera Portuguesa",
      },
      {
        name: "Sintra",
        subtitle: "PALÁCIOS E FLORESTAS",
        desc:
          "Em Sintra a arquitetura e a paisagem encontram-se de forma teatral. Os palácios e as quintas históricas ficam dentro de uma serra classificada pela UNESCO, o que favorece cerimónias ao fim do dia e jantares à luz das velas.",
        linkText: "Descobrir Sintra →",
        alt: "Sintra como destino de casamento: palácio na serra rodeado de floresta",
      },
      {
        name: "Porto",
        subtitle: "ENCANTO À BEIRA-RIO",
        desc:
          "O Porto tem uma textura urbana em camadas, com edifícios sobre a ribeira, igrejas revestidas a azulejo, armazéns convertidos em hotéis de design e caves de vinho viradas para o Douro. A cultura do vinho convive aqui com a sofisticação de quem vive à beira do rio.",
        linkText: "Explorar o Porto →",
        alt: "Porto como destino de casamento: ribeira e caves de vinho sobre o Douro",
      },
      {
        name: "Algarve",
        subtitle: "FALÉSIA E COSTA",
        desc:
          "O Algarve tem praia dourada, falésia e o Atlântico à frente. A leveza de andar descalço convive com precisão estrutural, porque o vento, o sol, os acessos e as marés decidem o desenho da festa.",
        linkText: "Planear o Algarve →",
        alt: "Algarve como destino de casamento: falésia dourada sobre o Atlântico",
      },
      {
        name: "Vale do Douro",
        subtitle: "QUINTAS COM VINHA",
        desc:
          "O Vale do Douro tem vinha em socalcos, quintas históricas e vista sobre o rio, o que pede fins de semana mais lentos e mais imersivos. A cultura do vinho e o património construído encontram-se nesta paisagem classificada pela UNESCO.",
        linkText: "Explorar o Douro →",
        alt: "Vale do Douro como destino de casamento: vinha em socalcos sobre o rio",
      },
    ],
    viewAllLocations: "Ver todas as regiões",
    mastheadIssue: "N.º 47 · Primavera/Verão",
    mastheadPlace: "Portugal · MMXXV",
    mastheadBrand: "Event Boutique",
    mastheadSpecial: "Especial Casamentos",
    whyHeadlineLine1: "Porque os casais que valorizam a atmosfera",
    whyHeadlineLine2: "estão a escolher Portugal",
    whyDeck: "Luz · Património · Arquitetura · Luxo Sereno",
    whyIntro:
      "Portugal passou a ser um cenário muito procurado para casamentos de destino de luxo, sobretudo entre casais dos Estados Unidos, do Reino Unido e do resto da Europa que valorizam a atmosfera acima da ostentação. O país reúne uma história em camadas, paisagens variadas e uma cultura de hospitalidade que é ao mesmo tempo calorosa e discreta.",
    insights: [
      {
        label: "01 — Luz",
        text:
          "A qualidade da luz define grande parte do que Portugal tem para oferecer. Junto à costa, a claridade suave do Atlântico e as longas horas douradas moldam as cerimónias ao ar livre. No interior, a luz filtrada pelas árvores cria uma atmosfera recolhida.",
      },
      {
        label: "02 — Património",
        text:
          "Os palácios, as quintas e as propriedades com séculos de história trazem azulejos, pedra lavrada, escadarias de grande efeito e jardins de claustro. Construímos uma produção feita à medida que respeita e enquadra estes elementos.",
      },
      {
        label: "03 — Luxo Sereno",
        text:
          "O serviço e o ofício são levados a sério, sem formalidade desnecessária. As celebrações vivem-se com naturalidade e mantêm-se rigorosas nos bastidores.",
      },
    ],
    whyBridge1:
      "Da Riviera Portuguesa à neblina sobre as florestas de Sintra e ao ritmo lento do Vale do Douro, cada região oferece um registo emocional diferente. Portugal não é um cenário genérico, mas uma tela estrutural para o design de eventos premium.",
    whyBridge2:
      "Lisboa e o Porto são portas de entrada internacionais bem servidas de ligações, mas, assim que os convidados chegam, palácios históricos, villas à beira-mar, vinhas e hotéis de design contemporâneo coexistem a poucas horas de carro.",
    whyClose:
      "Isto está próximo da nossa própria filosofia de celebrações intencionais e de confiança discreta, em que cada elemento foi pensado, colocado e temporizado com precisão arquitetónica.",
    philosophyLabel: "A nossa filosofia",
    philosophyHeadlineLine1: "Como pensamos a produção",
    philosophyHeadlineLine2: "de casamentos de luxo",
    philosophyParagraphs: [
      "Na Event Boutique, o trabalho é produção de casamentos. Produzir é orquestrar o espaço, a luz, o som, o movimento e a hospitalidade numa experiência coerente e integrada.",
      "O luxo, neste contexto, não está no excesso. Está na precisão estrutural e numa contenção deliberada.",
      "Cada projeto é construído de raiz, à medida de um casal, de um conjunto de convidados e de um lugar.",
    ],
    philosophyPrinciples: [
      "Menos elementos no desenho do dia, escolhidos e colocados com intenção.",
      "Cronogramas focados, que respeitam a energia de quem está presente.",
      "Comunicação clara e respeitosa.",
      "Sistemas técnicos que se apagam em segundo plano.",
    ],
    brideAlt: "Noiva a descer a escadaria do palácio, retrato editorial",
    brideCaption: "Palácio de Queluz, Sintra · Estilismo da Event Boutique",
    footerRegions: "Lisboa · Cascais · Sintra · Porto · Algarve · Vale do Douro",
    footerContinued: "Continua nas páginas 4–5 →  Event Boutique · desde 2022",
    legalEyebrow: "Informação importante",
    legalH2: "Clareza legal e logística",
    legalIntro:
      "É nos requisitos legais e na logística que se concentra, muitas vezes, a ansiedade dos casais internacionais.",
    legalLead: "Para o enquadramento legal, consultem o guia de",
    legalLinkText: "Requisitos Legais",
    legalTail: "que preparámos.",
    faqLead: "Para as questões práticas, as",
    faqLinkText: "Perguntas Frequentes",
    faqTail: "reúnem respostas estruturadas.",
    journeyH2: "Comecem o vosso percurso",
    journeyIntro:
      "Se reconhecem nesta abordagem os vossos próprios valores, o passo seguinte é simples.",
    ctaWork: "Ver o nosso trabalho",
    ctaPlanning: "Começar o planeamento",
    steps: [
      {
        title: "Descoberta",
        text:
          "Na fase de Descoberta, procuramos perceber como vivem, quais são as vossas referências estéticas e o que é prioritário na forma como a celebração deve ser sentida. Falamos também das questões práticas, das regiões que vos atraem, da distância que os convidados terão de percorrer e do grau de formalidade que vos parece natural.",
      },
      {
        title: "Curadoria",
        text:
          "Na Curadoria, reduzimos um país inteiro a um conjunto de caminhos viáveis, duas ou três localizações que correspondem de facto ao vosso pedido, cada uma com orçamentos de referência e implicações logísticas. Em vez de enviarmos uma lista longa de ligações, trabalhamos como editores, para que possam comparar cenários reais.",
      },
      {
        title: "Desenho e Produção",
        text:
          "No Desenho e Produção, combinamos um estilismo de eventos de base arquitetónica com a realidade dos calendários de montagem, das licenças e das restrições técnicas. A experiência mostra-nos que quem planeia a partir do estrangeiro precisa de criatividade e de estrutura clara, com moodboards, plantas, diagramas de iluminação e caminhos críticos que permitem visualizar o que vai acontecer, e quando.",
      },
      {
        title: "Legado",
        text:
          "Por fim, no Legado, pensamos para além dos dias do evento. A forma como a fotografia é agendada, os ângulos escolhidos, o encadeamento dos discursos e das transições, tudo isso determina como a celebração vai viver na memória e nas imagens.",
      },
    ],
  },
};
